import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import FieldNames from "../defaults/FieldNames.json";
import {emitCustomEvent} from "react-custom-events";
import Events from "../defaults/Events";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import store from "../redux/store";

export function generateFeatureCollectionFromShapefile(file, config) {
    var params = {
        name: file,
        targetSR: new SpatialReference(4269),
        maxRecordCount: 1000,
        enforceInputFileSizeLimit: true,
        enforceOutputJsonSizeLimit: true
    };

    //TODO: These are being overwritten by the API call
    params.generalize = false;
    params.maxAllowableOffset = 5;
    params.reducePrecision = false;

    return window.api.generateFeatureFromShapefile(params, file, config);
}

export function createFeatureCompleted(geometry, callbackParams) {
    let newFeature = Object.assign({}, callbackParams.feature);
    if (newFeature) {
        newFeature.geometry = geometry.toJSON();
        newFeature.isTreatment = callbackParams.isTreatment;
        newFeature.joinFeature = callbackParams.joinFeature;
        newFeature.isPostFire = callbackParams.isPostFire;
        newFeature.isProject = callbackParams.isProject;
    }

    if (callbackParams.isPoint) {
        newFeature.isPoint = true;
    }

    emitCustomEvent(callbackParams.customEvent, newFeature);
    if (callbackParams.isTreatment) {
        callbackParams.onDrawComplete("treatment");
    } else if (callbackParams.isProject) {
        callbackParams.onDrawComplete("project");
    } else {
        callbackParams.onDrawComplete("activity");
    }
}

export function getEventAndJoinFields(type, isPostFire = false, isTreatment = false, isCreatingProject = false) {
    const unsupportedTypes = ["multipoint", "extent", "mesh"];
    // not setup to handle anything besides point, line, polygon
    if (unsupportedTypes.includes(type)) {
        throw new Error(`Unsupported geometry type: ${type}`);
    }

    const defaultParams = {
        event: Events.EstimatedPolygonCreated,
        joinField: FieldNames.joinFeatureValues.polygon,
        isPoint: false,
        isProject: isCreatingProject
    };

    const operationType = getOperationType(isPostFire, isTreatment, isCreatingProject);
    if (operationType.allowedGeometries.includes(type)) {
        switch (type) {
            case "point":
                return {
                    event: Events.EstimatedPointCreated,
                    joinField: FieldNames.joinFeatureValues.point,
                    isPoint: true
                }
            case "polyline":
                return {
                    event: Events.EstimatedLineCreated,
                    joinField: FieldNames.joinFeatureValues.line,
                    isPoint: false
                }
            //defaults to polygon
            default:
                return defaultParams;
        }
    } else {
        throw new Error(`Unsupported geometry type "${type}" provided; [${operationType.allowedGeometries}] allowed for ${operationType.name}.`, {cause: 'GeometryTypeMismatch'});
    }
}

export function mapGeomTypes(type) {
    const geomTypes = {
        "esriGeometryPoint": "point",
        "esriGeometryPolyline": "polyline",
        "esriGeometryPolygon": "polygon",
        "esriGeometryMultipoint": "multipoint",
        "esriGeometryExtent": "extent",
        "esriGeometryMesh": "mesh"
    };

    if (geomTypes[type]) {
        return geomTypes[type];
    } else {
        throw new Error(`Unsupported geometry type: ${type}`);
    }
}

export function checkIfPointBuffer(tempPerimeter, isUploadPostFire, isTreatment, isProject = false) {
    return (tempPerimeter.type === 'point' && !isUploadPostFire && !isTreatment && !isProject) ?
        createGeodesicBufferFromPoint(tempPerimeter) :
        tempPerimeter
}

function createGeodesicBufferFromPoint(tempPerimeter) {
    const perimeterGeometry = geometryJsonUtils.fromJSON(tempPerimeter);
    return geometryEngine.geodesicBuffer(perimeterGeometry, 7000, 'feet');
}

// TODO: 'isPostFire' is piped in from several different variables - recommend standardizing and importing from redux
export function getOperationType(isPostFire, isTreatment, isProject) {
    const operationTypes = {
        newFuelsTreatment: {
            allowedGeometries: ['polygon'],
            name: 'New Fuels Treatment'
        },
        newFuelsActivity: {
            allowedGeometries: ['polygon', 'point'],
            name: 'New Fuels Activity'
        },
        postFireTreatment: {
            allowedGeometries: ['polygon', 'polyline', 'point'],
            name: 'Post Fire Treatment'
        },
        postFireActivity: {
            allowedGeometries: ['polygon', 'point'],
            name: 'Post Fire Activity'
        },
        newProject: {
            allowedGeometries: ['polygon'],
            name: 'New Project'
        }
    }

    if (isProject) {
        return operationTypes.newProject;
    } else {
        if (isPostFire) {
            return (isTreatment) ? operationTypes.postFireTreatment : operationTypes.postFireActivity;
        } else {
            return (isTreatment) ? operationTypes.newFuelsTreatment : operationTypes.newFuelsActivity;
        }
    }
}

export function getServiceLayerId (geometryType, config) {
    try {
        let geoLayerID = null;
        if (geometryType === FieldNames.joinFeatureValues.polygon) {
            geoLayerID = config.ifprsPolygons[config.env].layerId;
        } else if (geometryType === FieldNames.joinFeatureValues.point) {
            geoLayerID = config.ifprsPoints[config.env].layerId;
        } else if (geometryType === FieldNames.joinFeatureValues.line) {
            geoLayerID = config.ifprsLines[config.env].layerId;
        } else {
            throw new Error("Geometry type is invalid");
        }
        return geoLayerID
    } catch(e) {
        console.error(e);
        return null;
    }
}